import { Button, ButtonGroup, Classes, ControlGroup, Divider, FormGroup, H4, H5, InputGroup, Intent, MenuItem, NonIdealState, Tooltip } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { IItemRendererProps, Select } from "@blueprintjs/select";
import { IFieldProps } from 'decorator-form';
import * as React from "react";
import { connect } from 'react-redux';
import ComponentRegistry from "react-registry/dist/lib/ComponentRegistry"; // TODO update registry for proper access
import { bindActionCreators, Dispatch } from 'redux';

import * as configActions from 'core/actions/configActions';
import * as editActions from 'core/actions/editActions';
import initialState from "core/reducers/initialState";
import AppToaster from "core/util/AppToaster";
import { arrayMove } from 'core/util/ArrayUtils';

interface IExistingPanel {
	id: string;
	instance: string;
	editName: string;
	intent?: Intent;
}

type PanelsFieldOwnProps = IFieldProps<string[]>;

interface IPanelsFieldStateProps {
	selected: IExistingPanel[];
	existing: IExistingPanel[];
}

interface IPanelsFieldDispatchProps {
	actions: typeof configActions & typeof editActions;
}

function mapStateToProps(state: typeof initialState = initialState, ownProps: PanelsFieldOwnProps): IPanelsFieldStateProps {

	return {

		existing: Object.entries(state.config.panels || {}).reduce((lst, [instance, config]: [string, any]) => {
			if (!ownProps.formData || ownProps.formData.indexOf(instance) < 0) {
				lst.push({
					id: config.id,
					instance,
					editName: config['__editName'] || config.editName || 'Untitled Panel'
				});
			}
			return lst;
		}, []),

		selected: ownProps.formData ? ownProps.formData.map((instance, index) => {
			const sPanel = state.config.panels[instance];
			if (!sPanel) {
				return {
					id: 'invalid',
					instance,
					editName: "Invalid Panel Instance",
					intent: Intent.DANGER
				};
			}

			return {
				id: sPanel.id,
				instance,
				editName: sPanel['__editName'] || sPanel.editName,
			};
		}) : []

	};
}

function mapDispatchToProps(dispatch: Dispatch<typeof initialState>) {
	return {
		actions: {
			...bindActionCreators(configActions, dispatch),
			...bindActionCreators(editActions, dispatch)
		}
	};
}

type ResultProps = IPanelsFieldStateProps & IPanelsFieldDispatchProps & PanelsFieldOwnProps;

interface IPanelsFieldState {
	panelId: string | null;
	editName: string;
	available: string[];
}

const styles = {
	selected: {
		display: 'flex',
		justifyContent: 'space-between',
		paddingBottom: '5px'
	}
};

class PanelsField extends React.Component<ResultProps, IPanelsFieldState> {

	public constructor(props: ResultProps) {
		super(props);
		this.state = {
			available: Object.keys((ComponentRegistry.getInstance().getRegistry("panels") as any).registry.map),
			panelId: null,
			editName: ""
		};
	}

	public render() {
		const { label, selected } = this.props;

		const readyToAdd: boolean = !!this.state.panelId && !!this.state.editName;

		return (
			<FormGroup>
				<H4>{label}</H4>
				<FormGroup label="Panels">
					{
						selected.length > 0 ? selected.map(this.renderSelected) : <NonIdealState title="No Panels Selected" description="Choose existing or create some new ones" />
					}
				</FormGroup>
				<FormGroup label="Add New">
					<ControlGroup fill={true}>
						<Select
							items={this.state.available || []}
							itemRenderer={this.renderAvailable}
							itemPredicate={this.availablePredicate}
							onItemSelect={(panelId) => this.setState({panelId})}
						>
							<Button className={Classes.TEXT_OVERFLOW_ELLIPSIS} text={this.state.panelId || "Select a Panel Type"} />
						</Select>
						<InputGroup
							placeholder="(add editor name)"
							required={true}
							onChange={(e: any) => this.setState({editName: e.target.value})}
							value={this.state.editName}
						/>
						<Button
							disabled={!readyToAdd}
							intent={readyToAdd ? Intent.PRIMARY : Intent.NONE}
							onClick={this.addNewPanel}
							icon={IconNames.ADD}
						/>
					</ControlGroup>
				</FormGroup>
				<FormGroup label="Add Existing">
					<Select
						items={this.props.existing}
						itemRenderer={this.renderExisting}
						itemPredicate={this.existingPredicate}
						onItemSelect={({instance}) => this.addExistingPanel(instance)}
					>
						<Button text="Choose From Existing" />
					</Select>
				</FormGroup>
				<Divider />
			</FormGroup>
		);
	}

	private addNewPanel = () => {
		const { editName, panelId } = this.state;
		if (!editName || !panelId) {
			AppToaster.show({message: "Panel Type and Editor Name are required.", intent: Intent.WARNING});
			return;
		}

		this.props.actions.configPanelCreate(panelId, {editName}, (instance) => (
			this.setState({editName: "", panelId}, () => {
				this.props.onChange([...this.props.formData || [], instance]);
			})
		));
	}

	private addExistingPanel = (instance: string) => {
		this.props.onChange([...this.props.formData || [], instance]);
	}

	private goToPanel = (instance: string) => () => {
		this.props.actions.editSetActive({type: 'panel', instance});
	}

	private renderSelected = (selected: IExistingPanel, index: number, arr: IExistingPanel[]) => {

		return (
			<div style={styles.selected} key={selected.instance}>
				<div>
					<p><strong>{selected.editName}</strong></p>
					<p className={Classes.TEXT_MUTED + ' ' + Classes.TEXT_SMALL}>{`(${selected.id})`}</p>
				</div>
				<ButtonGroup minimal={true}>
					<Tooltip content="Go to this panel">
						<Button small={true} icon={IconNames.EDIT} onClick={this.goToPanel(selected.instance)} />
					</Tooltip>
					<Button small={true} icon={IconNames.CHEVRON_UP} disabled={index < 1} onClick={this.sortPanel(index, index - 1)} />
					<Button small={true} icon={IconNames.CHEVRON_DOWN} disabled={index >= arr.length - 1} onClick={this.sortPanel(index, index + 1)} />
					<Button small={true} icon={IconNames.SMALL_CROSS} onClick={this.removePanel(index)} />
				</ButtonGroup>
			</div>
		);
	}

	private renderExisting = (existing: IExistingPanel, {modifiers, handleClick}: IItemRendererProps) => (
		<MenuItem
			active={modifiers.active}
			key={existing.instance}
			onClick={handleClick}
			text={existing.editName}
			label={`(${existing.id})`}
		/>
	)

	private existingPredicate = (query: string = "", {editName = "", id = ""}: IExistingPanel = {} as IExistingPanel) => {
		const q = query.toLowerCase();
		return editName.toLowerCase().indexOf(q) > -1 || id.toLowerCase().indexOf(q) > -1;
	}

	private renderAvailable = (panelId: string, {modifiers, handleClick}: IItemRendererProps) => (
		<MenuItem
			active={modifiers.active}
			key={panelId}
			onClick={handleClick}
			text={panelId}
		/>
	)

	private removePanel = (index: number) => () => this.props.onChange((this.props.formData || []).filter((_, i) => i !== index));

	private sortPanel = (index: number, targetIndex: number) => () => this.props.onChange(arrayMove([...this.props.formData], index, targetIndex));

	private availablePredicate = (query: string = "", panelId: string = "") => panelId.toLowerCase().indexOf(query.toLowerCase()) > -1;
}

export default connect(mapStateToProps, mapDispatchToProps)(PanelsField);




// WEBPACK FOOTER //
// ./src/common/form/fields/PanelsField.tsx